import { forwardRef, useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import type { IFileAssetProps } from '~/components/molecules/FileAsset';
import type {
  IFileDropZoneFileInfo,
  IFileDropZonePartialFileInfo,
  IFileDropZoneProps,
} from '~/components/molecules/FileDropZone';
import type {
  IApiResourceFormat,
  IApiResourceType,
} from '~/lib/api/modules/AppResources';
import type { IApiFileUploadProgressEvent } from '~/lib/api/modules/Files';
import { FileDropZone } from '~/components/molecules/FileDropZone';
import { useApi } from '~/lib/api';
import { onboardingStatusQueryKeyFactory } from '~/lib/api/modules/Onboarding/hooks/useOnboardingStatusQuery';

export type { IFileDropZoneFileInfo } from '~/components/molecules/FileDropZone';

export type IAcceptableSize =
  | number
  | {
      min?: number;
      max?: number;
    };

const isAcceptableSize = (
  size: number,
  acceptableSize?: IAcceptableSize,
): boolean => {
  if (acceptableSize === undefined) {
    return true;
  }

  if (typeof acceptableSize === 'number') {
    return size === acceptableSize;
  }

  const { min, max } = acceptableSize;

  if (min !== undefined && size < min) {
    return false;
  }

  if (max !== undefined && size > max) {
    return false;
  }

  return true;
};

export type IAssetUploaderProps = Omit<
  IFileDropZoneProps,
  'files' | 'maxFiles'
> & {
  appId: string;
  type: IApiResourceType;
  format: IApiResourceFormat;
  acceptWidth?: IAcceptableSize;
  acceptHeight?: IAcceptableSize;
  downloadUrl?: string;
  onChange?: (hasAsset?: boolean) => void;
  renderFileAsset?: (props: IFileAssetProps) => React.ReactNode;
};

export const AssetUploader = forwardRef<HTMLInputElement, IAssetUploaderProps>(
  function FileUploader(props, ref) {
    const {
      appId,
      type,
      format,
      acceptWidth,
      acceptHeight,
      downloadUrl,
      onChange,
      renderFileAsset,
      ...fileDropZoneProps
    } = props;

    const api = useApi();
    const queryClient = useQueryClient();
    const [fileInfo, setFileInfo] = useState<IFileDropZonePartialFileInfo>();
    const assetId = useId();
    const { t } = useTranslation();

    useEffect(() => {
      // This component is controlled, which means that the downloadUrl prop is
      // always the source of truth. When the value prop changes, the component
      // should update the file info (that contains the updated file information
      // and status).
      setFileInfo(
        downloadUrl
          ? (fileInfo) => ({
              key: assetId,
              id: assetId,
              thumbUrl: downloadUrl,
              downloadUrl,
              loading: false,
              canRetry: false,
              ...fileInfo,
            })
          : undefined,
      );
    }, [assetId, downloadUrl]);

    const disabled = fileDropZoneProps.disabled;

    const handleProgressEvent = useCallback(
      (progressEvent: IApiFileUploadProgressEvent): void =>
        setFileInfo((fileInfo) =>
          fileInfo
            ? {
                ...fileInfo,
                loading: true,
                progress: progressEvent.progress,
              }
            : undefined,
        ),
      [],
    );

    const uploadFile = useCallback(
      (acceptedFileInfo: IFileDropZoneFileInfo) => {
        const acceptedFile = acceptedFileInfo.file;
        if (!acceptedFile) {
          setFileInfo(
            (fileInfo) => fileInfo ?? { ...acceptedFileInfo, stale: true },
          );

          return;
        }

        setFileInfo({
          ...acceptedFileInfo,
          loading: true,
          errorText: undefined,
        });

        void api.appResources
          .prepareUpload(appId, {
            resourceTypes: type,
            resourceFormats: format,
          })
          .then((response) => {
            const uploadUrl = response.urls?.[type]?.[format];
            if (!uploadUrl) {
              throw new Error('Upload URL not found.');
            }

            return api.files.upload(
              acceptedFile,
              uploadUrl,
              (progressEvent) => handleProgressEvent(progressEvent),
              acceptedFileInfo.abortController,
            );
          })
          .then(async () => {
            setFileInfo((fileInfo) =>
              fileInfo
                ? {
                    ...fileInfo,
                    loading: false,
                    stale: false,
                  }
                : undefined,
            );

            onChange?.(true);

            await queryClient.invalidateQueries({
              queryKey: onboardingStatusQueryKeyFactory({
                body: {
                  target: 'application',
                  id: appId,
                },
              }),
            });
          })
          .catch((error: Error) =>
            setFileInfo((fileInfo) =>
              fileInfo
                ? {
                    ...fileInfo,
                    loading: false,
                    errorText: error.message,
                    stale: true,
                    canRetry: true,
                  }
                : undefined,
            ),
          );
      },
      [api, appId, format, type, handleProgressEvent, onChange, queryClient],
    );

    const handleAcceptedDrop = (
      acceptedFilesInfo: Array<IFileDropZoneFileInfo>,
    ): void => {
      const acceptedFileInfo = acceptedFilesInfo[0];
      if (!acceptedFileInfo.file) {
        return;
      }

      if (acceptWidth || acceptHeight) {
        const image = new Image();

        image.onload = () => {
          if (!acceptedFileInfo.file) {
            return;
          }

          const reader = new FileReader();
          reader.readAsDataURL(acceptedFileInfo.file);
          reader.onload = () => {
            const { width, height } = image;
            if (
              isAcceptableSize(width, acceptWidth) &&
              isAcceptableSize(height, acceptHeight)
            ) {
              void uploadFile(acceptedFileInfo);
            } else {
              setFileInfo({
                ...acceptedFileInfo,
                loading: false,
                errorText: t('AssetUploader.errors.invalidImageSize'),
                canRetry: false,
              });
            }
          };
        };

        image.src = URL.createObjectURL(acceptedFileInfo.file);
      } else {
        void uploadFile(acceptedFileInfo);
      }
    };

    const handleRejectedDrop = (
      rejectedFilesInfo: Array<IFileDropZoneFileInfo>,
    ): void => {
      const rejectedFileInfo = rejectedFilesInfo[0];
      if (!rejectedFileInfo) {
        return;
      }

      setFileInfo(
        (fileInfo) => fileInfo ?? { ...rejectedFileInfo, stale: true },
      );
    };

    const handleRetry = (fileInfo: IFileDropZoneFileInfo) =>
      void uploadFile(fileInfo);

    // TODO call endpoint DELETE /files/:id when it is available to clean server
    // resources and bucket object
    // const handleDelete = (fileInfo: IFileDropZonePartialFileInfo): void => {
    //   fileInfo.abortController?.abort();
    //   setFileInfo(undefined);
    //   onChange?.(false);
    // };

    return (
      <FileDropZone
        {...fileDropZoneProps}
        ref={ref}
        files={fileInfo ? [fileInfo] : undefined}
        maxFiles={1}
        onAcceptedDrop={handleAcceptedDrop}
        onRejectedDrop={handleRejectedDrop}
        accept={
          fileDropZoneProps.accept ?? {
            'image/*': [],
          }
        }
        errorText={fileInfo?.errorText}
        onRetry={handleRetry}
        disabled={disabled}
        renderFileAsset={renderFileAsset}
      />
    );
  },
);
